import { DEFAULT_POLL_DATA, POLLS_TABLE_HEADER } from '@bo-constants/polls';
import { PaginationButtons, PrimaryButton } from '@library/components/buttons';
import { TableBody, TableHeader } from '@library/components/table';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Loading } from '@library/components/Loading';
import { PageTitle } from '@library/components/PageTitle';
import { PlusIcon } from '@heroicons/react/24/outline';
import type { PollData } from '@library/types/polls';
import { PollsDetailsModal } from '@bo-components/polls/PollsDetailsModal';
import { PollsFilters } from '@bo-components/polls/PollsFilters';
import { PollsTableRow } from '@bo-components/polls/PollsTableRow';
import { StyledTable } from '@bo-components/common/StyledTable';
import { WinnerSelectModal } from '@bo-components/polls/WinnerSelectModal';
import { usePolls } from '@bo-hooks/polls/usePolls';

export const PollsDashboard = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDate, setSelectedDate] = useState<string | undefined>();
  const {
    isError,
    polls,
    isLoading,
    isEditingPoll,
    isCreatingPoll,
    createPollError,
    editPollError,
    createPollSuccess,
    editPollSuccess,
    handleCreatePoll,
    handleEditPoll,
    totalPages,
    refetchPolls
  } = usePolls(currentPage, selectedDate);

  const [isNewItem, setIsNewItem] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [currentItemData, setCurrentItemData] =
    useState<PollData>(DEFAULT_POLL_DATA);
  const [modalReadOnly, setModalReadOnly] = useState(false);

  const handleChangeDateFilter = useCallback(
    (value?: string) => {
      setSelectedDate(value);
      setCurrentPage(1);
    },
    [setSelectedDate, setCurrentPage]
  );

  const [winnerModalOpen, toggleWinnerModalOpen] = useState(false);

  const handleCloseWinnerModal = useCallback(async () => {
    toggleWinnerModalOpen(false);
    setCurrentItemData(DEFAULT_POLL_DATA);
    await refetchPolls();
  }, [toggleWinnerModalOpen, setCurrentItemData, refetchPolls]);

  const handleOpenWinnerModal = useCallback((poll: PollData) => {
    toggleWinnerModalOpen(true);
    setCurrentItemData(poll);
  }, []);

  const handleAddNewItemClick = useCallback(() => {
    setIsDetailsModalOpen(true);
    setIsNewItem(true);
    setCurrentItemData(DEFAULT_POLL_DATA);
    setModalReadOnly(false);
  }, [setIsDetailsModalOpen, setIsNewItem, setCurrentItemData]);

  const handleCloseModal = useCallback(() => {
    setIsDetailsModalOpen(false);
    setIsNewItem(false);
    setCurrentItemData(DEFAULT_POLL_DATA);
    setModalReadOnly(false);
  }, [setIsDetailsModalOpen, setIsNewItem, setCurrentItemData]);

  const onSubmit = useCallback(
    (formData: FormData) => {
      if (
        !formData.get('poll[question]') ||
        !formData.get('poll[start_at]') ||
        !formData.get('poll[end_at]')
      )
        return;

      if (isNewItem) {
        handleCreatePoll(formData);
      } else {
        handleEditPoll(formData, currentItemData.id);
      }
    },
    [handleCreatePoll, handleEditPoll, isNewItem, currentItemData]
  );

  const submitMessage = useMemo(() => {
    if (isEditingPoll && editPollSuccess) {
      return 'Poll updated';
    }
    if (isCreatingPoll && createPollSuccess) {
      return 'Poll created';
    }
    return '';
  }, [isEditingPoll, createPollSuccess, isCreatingPoll, editPollSuccess]);

  const handleViewPollClick = useCallback(
    (poll: PollData) => {
      setModalReadOnly(true);
      setCurrentItemData(poll);
      setIsDetailsModalOpen(true);
    },
    [setModalReadOnly, setCurrentItemData, setIsDetailsModalOpen]
  );

  useEffect(() => {
    if (editPollSuccess || createPollSuccess) {
      handleCloseModal();
    }
  }, [editPollSuccess, createPollSuccess, handleCloseModal]);

  return (
    <div className="h-full min-w-full flex-col">
      <section className="border-soFresh-140 flex w-full flex-col justify-between px-6 pb-2 pt-3">
        <div className="pb-4">
          <PageTitle title="Polls" />
        </div>
        <div className="flex w-full justify-between pb-4">
          <div className="flex max-h-[44px] gap-4">
            <PollsFilters handleSelectDate={handleChangeDateFilter} />
          </div>
          <div className="flex max-h-full flex-col gap-4 self-center">
            <PrimaryButton handleOnClick={handleAddNewItemClick}>
              <div className="flex gap-2 text-sm">
                <PlusIcon
                  className="text-soClean-100 size-5"
                  data-testid="PlusIcon"
                />{' '}
                Add new
              </div>
            </PrimaryButton>
          </div>
        </div>
      </section>
      <div className="flex size-full gap-3 px-6">
        <div className="flex min-w-full flex-col gap-4">
          {isLoading ? (
            <Loading loadingMessage="Loading Polls" />
          ) : (
            <StyledTable
              errorMessage="Error Fetching Polls"
              isError={isError}
              noData={!polls?.length}
              noDataMessage={{
                label: "Couldn't find any Polls",
                title: 'No Polls',
                dataTestId: 'no-polls-results'
              }}
            >
              <TableHeader
                className="bg-soFresh-110 text-soClean-130 sticky top-0 flex w-full min-w-[1020px] items-center justify-start text-xs font-bold"
                headers={POLLS_TABLE_HEADER}
              />
              <TableBody>
                {polls?.map((poll) => {
                  const { id } = poll;
                  return (
                    <PollsTableRow
                      poll={poll}
                      key={`poll-${id}`}
                      handleViewPollClick={handleViewPollClick}
                      handleEditWinnerClick={handleOpenWinnerModal}
                    />
                  );
                })}
              </TableBody>
              {polls?.length > 0 && (
                <div className="bg-soFresh-100 border-soFresh-140 sticky bottom-0 flex w-full justify-end border-t">
                  <div className="flex items-center justify-between">
                    <PaginationButtons
                      currentPage={currentPage}
                      handlePageChange={setCurrentPage}
                      totalPages={totalPages}
                    />
                  </div>
                </div>
              )}
            </StyledTable>
          )}
        </div>
        <PollsDetailsModal
          isNewPoll={isNewItem}
          isOpen={isDetailsModalOpen}
          modalData={currentItemData}
          onClose={handleCloseModal}
          onSubmit={onSubmit}
          submitMessage={submitMessage}
          error={createPollError || editPollError}
          isLoading={isCreatingPoll}
          isSuccess={createPollSuccess || editPollSuccess}
          readOnly={modalReadOnly}
        />
        <WinnerSelectModal
          isOpen={winnerModalOpen}
          onClose={handleCloseWinnerModal}
          onSubmit={() => {
            setCurrentItemData(DEFAULT_POLL_DATA);
          }}
          poll={currentItemData}
        />
      </div>
    </div>
  );
};
